import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import LangSwitcher from "../LangSwitcher";

export default function Header() {
  const { t } = useTranslation();
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const NAV = [
    { to: "/boutique", label: t("nav.shop") },
    { to: "/categorie/meubles", label: t("nav.furniture") },
    { to: "/categorie/bijoux", label: t("nav.jewelry") },
    { to: "/categorie/artisanat", label: t("nav.crafts") },
    { to: "/revue", label: t("nav.revue") },
    { to: "/vip", label: t("nav.vip") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <span className="w-8 h-8 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-serif text-lg">
            M
          </span>
          <div className="leading-none">
            <div className="font-serif text-xl tracking-tight text-[#F5F5F0]">
              Maison Afra
            </div>
            <div className="overline text-[10px]">Luxe Afro-Européen</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-[#D4AF37]" : "text-[#A3A39D] hover:text-[#F5F5F0]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <LangSwitcher className="hidden md:flex" />

          {user && user !== false ? (
            <div className="hidden md:flex items-center gap-4">
              <Link
                to="/mes-commandes"
                data-testid="my-orders-link"
                className="text-[11px] uppercase tracking-[0.2em] text-[#A3A39D] hover:text-[#D4AF37]"
              >
                {t("nav.my_orders")}
              </Link>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="admin-link"
                  className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#F0C050]"
                >
                  {t("nav.admin")}
                </Link>
              )}
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                aria-label={t("nav.logout")}
                className="text-[#A3A39D] hover:text-[#D4AF37] transition-colors"
              >
                <LogOut strokeWidth={1.25} size={18} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="login-link"
              aria-label={t("nav.login")}
              className="hidden md:block text-[#A3A39D] hover:text-[#D4AF37] transition-colors"
            >
              <User strokeWidth={1.25} size={18} />
            </Link>
          )}

          <Link
            to="/panier"
            data-testid="cart-link"
            aria-label={t("nav.cart")}
            className="relative text-[#F5F5F0] hover:text-[#D4AF37] transition-colors"
          >
            <ShoppingBag strokeWidth={1.25} size={20} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-[10px] w-5 h-5 flex items-center justify-center font-medium"
              >
                {count}
              </span>
            )}
          </Link>

          <button
            className="lg:hidden text-[#F5F5F0]"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X strokeWidth={1.25} /> : <Menu strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t border-white/10">
          <nav className="flex flex-col py-4">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="px-6 py-3 text-sm uppercase tracking-[0.2em] text-[#A3A39D] hover:text-[#D4AF37]"
              >
                {item.label}
              </Link>
            ))}
            {user && user !== false ? (
              <>
                <Link
                  to="/mes-commandes"
                  onClick={() => setOpen(false)}
                  className="px-6 py-3 text-sm uppercase tracking-[0.2em] text-[#A3A39D]"
                >
                  {t("nav.my_orders")}
                </Link>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    onClick={() => setOpen(false)}
                    className="px-6 py-3 text-sm uppercase tracking-[0.2em] text-[#D4AF37]"
                  >
                    {t("nav.admin")}
                  </Link>
                )}
                <button
                  onClick={() => { setOpen(false); handleLogout(); }}
                  className="px-6 py-3 text-left text-sm uppercase tracking-[0.2em] text-[#A3A39D]"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="px-6 py-3 text-sm uppercase tracking-[0.2em] text-[#A3A39D]"
              >
                {t("nav.login")}
              </Link>
            )}
            <div className="px-6 py-3"><LangSwitcher /></div>
          </nav>
        </div>
      )}
    </header>
  );
}
